import { Component, Inject, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { finalize } from 'rxjs/operators';
import { WindowService } from 'src/app/shared/components/window/window.service';
import { createVertex, createVertexProperties, PoleModel, Vertex } from '../state/vertex.model';
import { VertexService } from '../state/vertex.service';

@Component({
  selector: 'app-vertex-form',
  templateUrl: './vertex-form.component.html',
  styleUrls: ['./vertex-form.component.scss']
})
export class VertexFormComponent implements OnInit {

  formVertex: FormGroup;

  isLoading: boolean;

  editionMode: boolean;

  removeConfirmated: boolean;

  MODELS = Object.values(PoleModel);

  MODELS_MAPPER = MODELS_MAPPER;

  constructor(
    @Inject(MAT_DIALOG_DATA) public vertex: Vertex,
    private formBuilder: FormBuilder,
    private vertextService: VertexService,
    private snakBar: MatSnackBar,
    private windowService: WindowService,
  ) {

    if (this.vertex.id) {
      this.editionMode = true;
    }
  }

  ngOnInit(): void {
    if (this.editionMode) {
      this.editionMode = true;
      this.vertextService.getFull(this.vertex.id)
        .pipe(finalize(() => this.isLoading = false))
        .subscribe(v => {
          this.vertex = v;
          this.createForm();
        });
    } else {
      this.createForm();
    }
  }

  save() {
    if (this.editionMode) {
      this.update();
    } else {
      this.insert();
    }
  }

  remove() {
    if (this.removeConfirmated) {
      this.vertextService.remove(this.vertex.id).subscribe(
        v => {
          this.snakBar.open($localize`Vertex "${this.vertex.id}" removed`, null, { duration: 2000 });
          this.windowService.close(this);
        },
        error => this.snakBar.open($localize`Error on Vertex "${this.vertex.id}" update`, 'CLOSE'),
      );
    }
    this.removeConfirmated = true;
  }

  private createForm() {
    const properties = createVertexProperties(this.vertex.properties);

    let validation: any[] | ((control: AbstractControl) => ValidationErrors);
    let numberValidation: any[] | ((control: AbstractControl) => ValidationErrors);
    let weightValidation: any[] | ((control: AbstractControl) => ValidationErrors);
    if (this.vertex.vertex_class === 'PL') {
      validation = Validators.required;
      numberValidation = [Validators.required, Validators.min(0)];
      weightValidation = [Validators.required, Validators.min(0), Validators.pattern('^[0-9]*$')];
    } else {
      validation = [];
      numberValidation = [];
      weightValidation = [];
    }

    this.formVertex = this.formBuilder.group({
      model: [properties.model, validation],
      height: [properties.height, numberValidation],
      weight: [properties.weight, weightValidation],
      comments: [properties.comments],
      has_switch: [properties.has_switch, validation],
      has_transformer: [properties.has_transformer, validation]
    });
  }

  private insert() {
    const vertex = this.formToData();
    this.vertextService.add(vertex).subscribe(
      v => {
        this.snakBar.open($localize`Vertex "${v.id}" created`, null, { duration: 2000 });
        this.windowService.close(this);
      },
      error => {
        this.snakBar.open($localize`Error on Vertex creation`, 'CLOSE');
      },
    );
  }

  private update() {
    const vertex = this.formToData();
    this.vertextService.update(vertex.id, vertex).subscribe(
      v => {
        this.snakBar.open($localize`Vertex "${vertex.id}" update`, null, { duration: 2000 });
        this.windowService.close(this);
      },
      error => this.snakBar.open($localize`Error on Vertex "${vertex.id}" update`, 'CLOSE'),
    );
  }

  private formToData() {
    const vertex = createVertex(this.vertex);
    vertex.properties = {
      ...this.formVertex.value
    };
    return vertex;
  }

}

const MODELS_MAPPER = {
  Circular: $localize`Circular`,
  'Double T': $localize`Double T`,
  Rectangular: $localize`Rectangular`,
  Wood: $localize`Wood`,
  Metalic: $localize`Metalic`,
  Other: $localize`Other`
};
